/**
 * client — the SPA's data layer: fetch and decode the daemon's read surface.
 *
 * Auth: the daemon authorizes every route except `GET /health`, and the browser's
 * credential is the oauth2-proxy session cookie. Nothing in this file attaches
 * it — an httpOnly cookie is attached by the browser or not at all — so every
 * call goes through {@link apiFetch}, which states the credential mode once and
 * turns a `401`/`403` into a typed {@link SessionError} the App renders as a
 * signed-out or not-permitted state. `POST /runs` (Observatory's trigger) keeps
 * its own machine bearer, which no browser code path touches.
 *
 * Each fetcher owns only its endpoint, its decode, and its non-identity status
 * semantics (`304`, `404`, `409`); the transport, timeout, and identity handling
 * are the chokepoint's.
 */

import { ReadApiError, apiFetch } from './api-fetch'
import type { TaggedResult } from './polling'
import type { RunsSnapshot, TrialDetail, TrialDiagnostics } from './types'

// Re-exported so the many `import { ReadApiError } from './client'` sites keep
// reading naturally: the error type belongs to the transport now, but it is
// still what a data-layer caller catches.
export { ReadApiError, SessionError, sessionFailureOf } from './api-fetch'

/**
 * Fetch the Run Overview snapshot from `GET /runs`. Throws {@link ReadApiError}
 * on a non-2xx response so the caller renders an honest error state naming the
 * endpoint rather than a spinner.
 */
export async function fetchRuns(fetchImpl: typeof fetch = fetch, timeoutMs = 10_000): Promise<RunsSnapshot> {
  const endpoint = '/runs'
  const res = await apiFetch({ endpoint, fetchImpl, timeoutMs })
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  return (await res.json()) as RunsSnapshot
}

/**
 * Fetch one trial's drawer detail from `GET /trials/<id>`. Same honest-error
 * discipline as {@link fetchRuns}: a non-2xx (including `404` for an unknown
 * trial) throws a {@link ReadApiError} naming the endpoint so the drawer renders
 * an error state, never a stuck spinner. The id is URL-encoded so a trial id
 * with reserved characters routes correctly.
 */
export async function fetchTrial(
  trialId: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<TrialDetail> {
  const endpoint = `/trials/${encodeURIComponent(trialId)}`
  const res = await apiFetch({ endpoint, fetchImpl, timeoutMs })
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  return (await res.json()) as TrialDetail
}

/**
 * Fetch a finished trial's Diagnostics link map from `GET /trials/<id>/diagnostics`
 * (Story K). Lazy by caller — the drawer hits this only when the Diagnostics tab
 * opens. Same honest-error discipline as {@link fetchTrial}: a non-2xx throws a
 * {@link ReadApiError} naming the endpoint so the tab renders an error state,
 * never a stuck spinner. An in-flight trial yields `{manifestUri: null,
 * objects: null}` (the daemon's fallback), which the tab renders as its own fallback.
 */
export async function fetchDiagnostics(
  trialId: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<TrialDiagnostics> {
  const endpoint = `/trials/${encodeURIComponent(trialId)}/diagnostics`
  const res = await apiFetch({ endpoint, fetchImpl, timeoutMs })
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  return (await res.json()) as TrialDiagnostics
}

/**
 * Resolve one evidence object to its short-lived presigned S3 URL
 * (`GET /trials/<id>/object/<key>`, Story K).
 *
 * The endpoint content-negotiates: `Accept: application/json` returns the URL in
 * a `200` body, anything else `302`s straight to S3. The session cookie is
 * `SameSite=Lax`, so it now rides a top-level navigation too — the `302` branch
 * would work from a bare anchor, which the bearer-era comment at
 * `src/daemon/http.ts` said it could not. The SPA still takes the JSON branch
 * deliberately: this endpoint's non-happy paths are states the drawer must
 * render in place — `409` for a run whose manifest is not finalized, `404` for a
 * missing object, `503` for an S3 fault, and now `401`/`403` for an expired or
 * unpermitted session. A navigation would answer all of those by replacing the
 * page (or a new tab) with raw JSON, and would route an expired session into a
 * detached sign-in outside the SPA's session state. Fetch-then-open keeps every
 * one of them inside the UI, and the bytes still flow browser→S3 either way
 * (Principle 1). Throws {@link ReadApiError} on a non-2xx so the click surfaces
 * an honest failure rather than a dead tab.
 */
export async function fetchObjectUrl(
  trialId: string,
  key: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<string> {
  const endpoint = `/trials/${encodeURIComponent(trialId)}/object/${encodeURIComponent(key)}`
  // `apiFetch` already pins `redirect: 'manual'`, which matters twice here: the
  // daemon must not be allowed to bounce this JSON read to S3 if the Accept
  // negotiation ever changes, and a proxy sign-in bounce must classify as
  // signed-out instead of failing CORS against Google.
  const res = await apiFetch({ endpoint, headers: { accept: 'application/json' }, fetchImpl, timeoutMs })
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  const { url } = (await res.json()) as { url: string }
  return url
}

/**
 * Fetch and decode a finished trial's `trajectory.json` for the drawer Timeline
 * tab (Story J). Two hops, reusing Story K's object interface verbatim rather
 * than re-resolving the manifest: first {@link fetchObjectUrl} hands back the
 * short-lived presigned S3 URL for the `trajectory` object (an authenticated
 * call through the daemon), then the bytes are read straight from S3 over that
 * URL — they never stream through the daemon (Principle 1). Returns the parsed
 * document as `unknown` for {@link parseTrajectory} to shape tolerantly; throws
 * {@link ReadApiError} on either hop's failure so the tab renders an honest
 * error state rather than a stuck spinner.
 */
export async function fetchTrajectory(
  trialId: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<unknown> {
  const url = await fetchObjectUrl(trialId, 'trajectory', fetchImpl, timeoutMs)
  const endpoint = `trajectory.json (${trialId})`
  let res: Response
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    // Deliberately NOT `apiFetch`: this hop leaves the daemon's origin. `url` is
    // a presigned S3 URL carrying its own SigV4 credential, so the session
    // cookie must not be sent (`credentials: 'omit'` — cross-origin, and S3 has
    // no use for it), and an S3 `403` from an EXPIRED presign must stay a plain
    // read failure. Classifying it as a session failure would tell an operator
    // their account is not permitted when the truth is a stale link.
    res = await fetchImpl(url, { method: 'GET', credentials: 'omit', signal: controller.signal })
  } catch (err) {
    throw new ReadApiError(endpoint, 0, err instanceof Error ? err.message : 'network error')
  } finally {
    clearTimeout(timeoutId)
  }
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET trajectory.json returned ${res.status}`)
  }
  return (await res.json()) as unknown
}

/**
 * Conditionally fetch the Run Overview snapshot for the cached-polling client.
 * Sends `If-None-Match` with the caller's last ETag and maps a `304 Not Modified`
 * to a `not-modified` result (no body re-decode), so an idle board transfers
 * almost nothing. A `200` returns the fresh snapshot plus its new ETag. Throws
 * {@link ReadApiError} on any other non-2xx so the polling client surfaces an
 * honest disconnected/error state rather than a spinner.
 */
export async function fetchRunsTagged(
  etag: string | null,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<TaggedResult<RunsSnapshot>> {
  const endpoint = '/runs'
  const headers: Record<string, string> = {}
  if (etag) {
    headers['if-none-match'] = etag
  }
  const res = await apiFetch({ endpoint, headers, fetchImpl, timeoutMs })
  const responseEtag = normalizeEtag(res.headers.get('etag')) ?? etag
  if (res.status === 304) {
    return { status: 'not-modified', etag: responseEtag, data: null }
  }
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  return { status: 'ok', etag: responseEtag, data: (await res.json()) as RunsSnapshot }
}

/** Strip the optional weak prefix and surrounding quotes from a response `ETag`. */
function normalizeEtag(raw: string | null): string | null {
  if (!raw) {
    return null
  }
  return raw.trim().replace(/^W\//, '').replace(/^"|"$/g, '')
}
